/* =====================================================================
 * B2C React Query client bootstrap.
 *
 * Phase 11-4i thinned this file to only what the host actually owns:
 *
 *   1. The singleton `QueryClient` with B2C-tuned stale / gc / retry
 *      defaults.
 *   2. A one-time `setBillingCacheAdapter()` registration so
 *      `@auw/stores` can read/fetch the billing status via React Query
 *      without directly depending on this package.
 *   3. `hydrate/dehydrate` helpers that persist the *account-scoped*
 *      snapshot into sessionStorage under a tenant-namespaced key so
 *      full-page reloads don't blank the sidebar.
 *
 * Everything else (`queryKeys`, `clearQuerySessionStorage`) now lives
 * in `@auw/workspace-utils` and is imported directly by consumers.
 * ===================================================================== */
import { QueryClient, dehydrate, hydrate } from '@tanstack/react-query';
import {
  getActiveAccountId,
  getBillingStatus,
  type BillingStatus,
} from '@auw/api-client';
import { setBillingCacheAdapter } from '@auw/stores';
import { activeTenantKey } from '@auw/tenant';
import { queryKeys } from '@auw/workspace-utils';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

/* ------------------------------------------------------------------ */
/* DI bridge — @auw/stores reads billing cache via this adapter        */
/* instead of depending on React Query directly. Registration happens */
/* at module-load time so the store can use it from the first render. */
/* ------------------------------------------------------------------ */

setBillingCacheAdapter({
  get: () =>
    queryClient.getQueryData<BillingStatus>(queryKeys.billing.status()) ?? null,
  fetch: () =>
    queryClient.fetchQuery({
      queryKey: queryKeys.billing.status(),
      queryFn: getBillingStatus,
      staleTime: 30 * 1000,
    }),
});

/* ------------------------------------------------------------------ */
/* sessionStorage persist — account + tenant scoped                    */
/* ------------------------------------------------------------------ */

/* Resolved lazily — see api-client/authVault.ts header for chunk-split
 * rationale on why module-eval `activeTenantKey(...)` is unsafe. */
let _rqCacheKey: string | null = null;
function getRqCacheKey(): string {
  if (_rqCacheKey === null) _rqCacheKey = activeTenantKey('rq_cache');
  return _rqCacheKey;
}
const MAX_AGE_MS = 10 * 60 * 1000;

interface PersistedSnapshot {
  accountId: string;
  state: ReturnType<typeof dehydrate>;
  ts: number;
}

export function hydrateFromSessionStorage(): void {
  try {
    const accountId = getActiveAccountId();
    if (!accountId) return;

    const raw = sessionStorage.getItem(getRqCacheKey());
    if (!raw) return;

    const snap = JSON.parse(raw) as PersistedSnapshot;
    if (snap.accountId !== accountId) return;
    if (Date.now() - snap.ts > MAX_AGE_MS) {
      sessionStorage.removeItem(getRqCacheKey());
      return;
    }

    hydrate(queryClient, snap.state);
  } catch {
    /* corrupted — ignore */
  }
}

export function dehydrateToSessionStorage(): void {
  try {
    const accountId = getActiveAccountId();
    if (!accountId) return;

    const state = dehydrate(queryClient, {
      shouldDehydrateQuery: (q) => q.state.status === 'success',
    });

    if (state.queries.length === 0) return;

    const snap: PersistedSnapshot = { accountId, state, ts: Date.now() };
    sessionStorage.setItem(getRqCacheKey(), JSON.stringify(snap));
  } catch {
    /* quota exceeded — ignore */
  }
}
